import { action, observable, reaction, makeObservable } from 'mobx';
import React from 'react';
import {deepEquals, Disposers, logger} from '../../../lib/helpers';
import {SimplePeerSignal, TurnServerCredential} from '../models/classroom-models';
import {ClassroomHubStore} from '../stores/ClassroomHubStore';
import {LocalVideoStore} from './LocalVideoStore';
import {isUserMediaReady} from './models';
import {ClassroomStoreWrapper} from './parts/ClassroomStoreWrapper';
import {SimplePeerStore} from './SimplePeerStore';

export class ClassroomVideoStore {
  readonly wrapper: ClassroomStoreWrapper;

  readonly localVideoRef = React.createRef<HTMLVideoElement>();
  readonly remoteVideoRef = React.createRef<HTMLVideoElement>();

  @observable.ref
  private connectionPair: string | null;
  readonly localMedia: LocalVideoStore;

  @observable.ref
  simplePeer: SimplePeerStore | null;

  @observable.ref
  credential: TurnServerCredential | null;

  private readonly disposers = new Disposers();

  constructor(readonly classroomHubStore: ClassroomHubStore) {
    makeObservable(this);
    this.wrapper = new ClassroomStoreWrapper(classroomHubStore);
    this.connectionPair = null;

    this.localMedia = new LocalVideoStore();

    this.simplePeer = null;

    this.credential = null;
    this.setup();

    // @ts-ignore
    window.localVideoRef = this.localVideoRef;
    // @ts-ignore
    window.remoteVideoRef = this.remoteVideoRef;
    // @ts-ignore
    window.videoStore = this;
  }

  @action
  setCredential = (credential: TurnServerCredential | null) => {
    this.credential = credential;
  };

  onReceivedSignal = (signal: SimplePeerSignal) => {
    this.simplePeer?.onReceivedSignal(signal);
  };

  private setup() {
    this.disposers.add(this.localMedia);

    const wrapper = this.wrapper;
    this.disposers.add(reaction(
      () => [wrapper.connectionPair],
      // access local user media
      this.onConnectionPairChanged
    ));

    this.disposers.add(reaction(
      () => [this.localMedia?.mediaStreamState?.state, wrapper.getLocalMediaState],
      // synchronize classroomState's teacherMediaState/studentMediaState
      this.updateClassroomMediaStreamState
    ));

    this.disposers.add(reaction(
      () => [
        this.localMedia.userMedia?.value,
        this.credential,
        wrapper.teacherMediaState,
        wrapper.studentMediaState],
      this.startSimplePeerConnection
    ));

    this.disposers.add(reaction(
      () => [this.localMedia.userMedia?.value],
      this.updateLocalVideoElementSource
    ));

    this.disposers.add(reaction(
      () => [this.simplePeer?.remoteStream],
      this.updateRemoteVideoElementSource));
  }

  @action
  private onConnectionPairChanged = () => {
    const connectionPair = this.wrapper.connectionPair;

    if (connectionPair === null)
      this.localMedia.closeUserMedia();
    else if (connectionPair !== this.connectionPair)
      this.localMedia.getUserMedia(true);
    else
      this.localMedia.getUserMedia();
    this.connectionPair = connectionPair;
  }

  @action
  private updateClassroomMediaStreamState = () => {
    let current = this.localMedia?.mediaStreamState?.state;
    let stored = this.wrapper.getLocalMediaState;
    if (current === undefined) {
      if (stored !== null)
        this.wrapper.updateLocalMediaState(null);
    }
    else if (!deepEquals(current, stored))
      this.wrapper.updateLocalMediaState(current);
  };

  @action
  startSimplePeerConnection = () => {
    logger.log('ClassroomVideoStore.startSimplePeerConnection');
    const w = this.wrapper;
    const stream = this.localMedia.userMedia?.value;
    const teacher = w.teacherConnectionId;
    const student = w.studentConnectionId;
    const credential = this.credential;
    const teacherMediaState = w.teacherMediaState;
    const studentMediaState = w.studentMediaState;

    if (stream === null || stream === undefined ||
      teacher === null || student === null ||
      credential === null ||
      !isUserMediaReady(teacherMediaState) || !isUserMediaReady(studentMediaState)) {
      this.simplePeer?.dispose();
      this.simplePeer = null;
      return;
    }

    if (this.simplePeer !== null &&
      stream.id === this.simplePeer.stream.id &&
      teacher === this.simplePeer.teacherConnectionId &&
      student === this.simplePeer.studentConnectionId &&
      this.simplePeer.simplePeerError === null
      /* todo: add additional checks */) {
      return;
    }
    this.simplePeer?.dispose();
    this.simplePeer = new SimplePeerStore(teacher, student,
      stream, credential, this.wrapper);
  };

  private updateLocalVideoElementSource = () => {
    if (this.localVideoRef.current === null)
      return;

    const stream = this.localMedia.userMediaWithoutAudio;
    if (stream === null || stream === undefined)
      this.localVideoRef.current.srcObject = null;
    else
      this.localVideoRef.current.srcObject = stream;
  };

  private updateRemoteVideoElementSource = () => {
    if (this.remoteVideoRef.current === null)
      return;

    const stream = this.simplePeer?.remoteStream;
    if (stream === null || stream === undefined)
      this.remoteVideoRef.current.srcObject = null;
    else
      this.remoteVideoRef.current.srcObject = stream;
  };

  dispose = () => {
    this.simplePeer?.dispose();
    this.simplePeer = null;
    this.disposers.dispose();
  };
}
